import React from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { fileUrl } from "@/lib/api";
import { money } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";

export default function CartSheet() {
  const { items, open, setOpen, updateQty, removeItem, subtotal, count } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="bg-[var(--bg)] border-l border-[var(--border-lch)] text-[var(--text)] w-full sm:max-w-md flex flex-col" data-testid="cart-sheet">
        <SheetHeader>
          <SheetTitle className="text-[var(--text)] flex items-center gap-2">
            <ShoppingBag size={18} className="text-[#c9a86a]" /> Your Cart ({count})
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {items.length === 0 ? (
            <div className="text-center text-[var(--muted)] py-16" data-testid="cart-empty">
              <ShoppingBag size={32} className="mx-auto mb-3 opacity-40" />
              Your cart is empty.
            </div>
          ) : (
            items.map((it) => (
              <div key={it.id} className="flex gap-3 border-b border-[var(--border-lch)] pb-4" data-testid={`cart-item-${it.id}`}>
                <div className="h-16 w-16 rounded bg-[var(--panel-2)] overflow-hidden shrink-0">
                  {it.image && <img src={fileUrl(it.image)} alt={it.name} className="h-full w-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[var(--text)] truncate">{it.name}</div>
                  <div className="text-[#c9a86a] text-sm mt-0.5">{money(it.price)}</div>
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => updateQty(it.id, it.qty - 1)} className="h-6 w-6 flex items-center justify-center border border-[var(--border-2)] rounded text-[var(--text-2)] hover:text-[var(--text)]" data-testid={`cart-dec-${it.id}`}><Minus size={12} /></button>
                    <span className="text-sm w-6 text-center" data-testid={`cart-qty-${it.id}`}>{it.qty}</span>
                    <button onClick={() => updateQty(it.id, it.qty + 1)} className="h-6 w-6 flex items-center justify-center border border-[var(--border-2)] rounded text-[var(--text-2)] hover:text-[var(--text)]" data-testid={`cart-inc-${it.id}`}><Plus size={12} /></button>
                    <button onClick={() => removeItem(it.id)} className="ml-auto text-[var(--muted)] hover:text-red-400" data-testid={`cart-remove-${it.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <SheetFooter className="flex-col gap-3 sm:flex-col">
            <div className="flex items-center justify-between w-full text-sm">
              <span className="text-[var(--text-2)]">Subtotal</span>
              <span className="text-[var(--text)] font-medium" data-testid="cart-subtotal">{money(subtotal)}</span>
            </div>
            <Button onClick={goCheckout} data-testid="cart-checkout-btn" className="w-full bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none">
              Proceed to Checkout
            </Button>
            <button onClick={() => setOpen(false)} className="text-xs text-[var(--muted)] hover:text-[var(--text-2)]">Continue shopping</button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}
